// MyVouchers.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  SafeAreaView,
  StyleSheet,
  View,
  Text,
  ScrollView,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Alert,
  Linking,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';

const API_URL = 'https://collabbay.com/APIControllers/FetchCreatorVouchers.php';
const LOGO_URL = 'https://collabbay.com/CollabbayLogoDark.png';
const IMAGE_BASE = 'https://collabbayadmin.com';
const FALLBACK_IMAGE =
  'https://images.pexels.com/photos/1058277/pexels-photo-1058277.jpeg';

const CREATOR_EARNINGS_PER_REDEMPTION = 5;

const normaliseImageUri = (image) => {
  if (!image || !String(image).trim()) return FALLBACK_IMAGE;

  const trimmed = String(image).trim();

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  if (trimmed.startsWith('/')) {
    return `${IMAGE_BASE}${trimmed}`;
  }

  return `${IMAGE_BASE}/${trimmed}`;
};

const formatNumber = (value) => {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n.toLocaleString('en-GB') : '0';
};

const formatMoney = (value) => {
  const n = Number(value || 0);
  return `£${Number.isFinite(n) ? n.toLocaleString('en-GB') : '0'}`;
};

const formatDate = (value) => {
  if (!value) return 'Recently';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatDealText = (voucher) => {
  const spend = Number(voucher.OnSpend || 0);
  const discount = Number(voucher.Discount || 0);

  if (spend > 0 && discount > 0) {
    return `Give your audience ${discount}% off when they spend £${spend}`;
  }

  if (discount > 0) {
    return `Give your audience ${discount}% off`;
  }

  return 'Creator voucher deal';
};

const openExternalLink = async (url) => {
  if (!url) return;

  try {
    await Linking.openURL(url);
  } catch (error) {
    Alert.alert('Could not open link', 'Please try again.');
  }
};

const getVoucherPurchaseUrl = (voucher) => {
  if (voucher?.PurchaseURL) return voucher.PurchaseURL;

  const collabID = String(voucher?.CollabID || '').trim();

  if (!collabID) return '';

  return `https://collabbay.com/Voucher/${encodeURIComponent(collabID)}`;
};

const getCreatorPublicPageUrl = (creatorUsername, suppliedUrl) => {
  if (suppliedUrl) return suppliedUrl;

  const promoter = String(creatorUsername || '').trim();

  if (!promoter) return '';

  return `https://collabbay.com/Vouchers/${encodeURIComponent(promoter)}`;
};

const BrandImage = ({ image, style }) => {
  const [uri, setUri] = useState(normaliseImageUri(image));

  useEffect(() => {
    setUri(normaliseImageUri(image));
  }, [image]);

  return (
    <Image
      source={{ uri }}
      style={style}
      onError={() => setUri(FALLBACK_IMAGE)}
    />
  );
};

export default function MyVouchers({ navigation }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [userData, setUserData] = useState({});
  const [creatorUsername, setCreatorUsername] = useState('');
  const [creatorPublicPageURL, setCreatorPublicPageURL] = useState('');

  const [vouchers, setVouchers] = useState([]);
  const [ledger, setLedger] = useState([]);

  const [summary, setSummary] = useState({
    TotalDeals: 0,
    TotalStockAssigned: 0,
    TotalRedeemed: 0,
    TotalRemaining: 0,
    EstimatedCreatorEarnings: 0,
  });

  const [activeTab, setActiveTab] = useState('stock');
  const [errorMessage, setErrorMessage] = useState('');

  const fetchUserData = async () => {
    try {
      const response = await fetch(
        'https://collabbay.com/APIControllers/GetSessionData.php',
        {
          method: 'GET',
          credentials: 'include',
        }
      );

      const data = await response.json();

      setUserData(data || {});
    } catch (error) {
      console.error('Fetch user data error:', error);
    }
  };

  const fetchCreatorVouchers = async () => {
    const response = await fetch(`${API_URL}?d=${Date.now()}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    const rawText = await response.text();

    let data;

    try {
      data = JSON.parse(rawText);
    } catch (error) {
      throw new Error(`Invalid voucher response: ${rawText.slice(0, 160)}`);
    }

    if (!response.ok || !data.success) {
      throw new Error(data.message || data.debug || 'Could not load vouchers.');
    }

    setCreatorUsername(data.Creator || '');
    setCreatorPublicPageURL(data.CreatorPublicPageURL || '');
    setVouchers(Array.isArray(data.vouchers) ? data.vouchers : []);
    setLedger(Array.isArray(data.ledger) ? data.ledger : []);
    setSummary(
      data.summary || {
        TotalDeals: 0,
        TotalStockAssigned: 0,
        TotalRedeemed: 0,
        TotalRemaining: 0,
        EstimatedCreatorEarnings: 0,
      }
    );
    setErrorMessage('');
  };

  const loadPage = async () => {
    try {
      await Promise.all([fetchUserData(), fetchCreatorVouchers()]);
    } catch (error) {
      console.error('Load creator vouchers error:', error);
      setErrorMessage(error.message || 'Could not load your vouchers.');
    }
  };

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      await loadPage();
      setLoading(false);
    };

    run();
  }, []);

  const onRefresh = async () => {
    setRefreshing(true);
    await loadPage();
    setRefreshing(false);
  };

  const displayUsername = creatorUsername || userData?.Username || '';

  const publicContentURL = getCreatorPublicPageUrl(
    displayUsername,
    creatorPublicPageURL
  );

  const totalEarnings = useMemo(() => {
    return Number(summary.TotalRedeemed || 0) * CREATOR_EARNINGS_PER_REDEMPTION;
  }, [summary.TotalRedeemed]);

  const liveVouchers = useMemo(() => {
    return vouchers.filter((item) => Number(item.Remaining || 0) > 0);
  }, [vouchers]);

  const redeemedVouchers = useMemo(() => {
    return vouchers.filter((item) => Number(item.Redeemed || 0) > 0);
  }, [vouchers]);

  const handleFooterPress = (routeName, fallbackUrl = null) => {
    if (routeName && navigation?.navigate) {
      navigation.navigate(routeName);
      return;
    }

    if (fallbackUrl) {
      openExternalLink(fallbackUrl);
    }
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <View style={styles.headerRow}>
        <Image source={{ uri: LOGO_URL }} style={styles.logo} />

        <View style={styles.headerText}>
          <Text style={styles.headerTitle}>My Vouchers</Text>
          <Text style={styles.headerSubtitle}>
            {displayUsername ? `Hi 👋, ${displayUsername}` : 'Creator dashboard'}
          </Text>
        </View>

        <TouchableOpacity
          style={styles.helpButton}
          activeOpacity={0.85}
          onPress={() =>
            Alert.alert(
              'Creator vouchers',
              'Brands can give you vouchers after a collab. Share your public page or individual purchase links with your audience, then track redemptions and estimated sales. Each redemption is currently shown as £5.'
            )
          }
        >
          <Ionicons name="help-circle-outline" size={22} color="#111" />
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderPublicPageCard = () => (
    <TouchableOpacity
      style={styles.sharePageCard}
      activeOpacity={0.9}
      onPress={() => openExternalLink(publicContentURL)}
      disabled={!publicContentURL}
    >
      <View style={styles.sharePageTopRow}>
        <View style={styles.sharePageBadge}>
          <Text style={styles.sharePageBadgeText}>SHARE MY CONTENT PAGE</Text>
        </View>

        <View style={styles.sharePageArrow}>
          <Ionicons name="arrow-forward-outline" size={17} color="#111" />
        </View>
      </View>

      <Text style={styles.sharePageTitle}>
        Share all your live voucher deals.
      </Text>

      <Text style={styles.sharePageCopy}>
        Send your followers one page where they can view and purchase your
        current brand vouchers.
      </Text>

      <View style={styles.shareUrlRow}>
        <Ionicons name="link-outline" size={15} color="#555" />
        <Text style={styles.shareUrlText} numberOfLines={1}>
          {publicContentURL || 'Your shareable content page'}
        </Text>
      </View>
    </TouchableOpacity>
  );

  const renderHero = () => (
    <View style={styles.hero}>
      <LinearGradient
        colors={['#181818', '#050505']}
        style={styles.heroInner}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
      >
        <Text style={styles.heroBadge}>CREATOR VOUCHERS</Text>

        <Text style={styles.heroTitle}>Your sales from shared deals.</Text>

        <Text style={styles.heroCopy}>
          Track live voucher supply, audience redemptions and estimated
          earnings from every brand deal you share.
        </Text>

        <View style={styles.heroStatsRow}>
          <View style={styles.heroStat}>
            <Text style={styles.heroStatValue}>
              {formatNumber(summary.TotalRemaining)}
            </Text>
            <Text style={styles.heroStatLabel}>Available</Text>
          </View>

          <View style={styles.heroStat}>
            <Text style={styles.heroStatValue}>
              {formatNumber(summary.TotalRedeemed)}
            </Text>
            <Text style={styles.heroStatLabel}>Redeemed</Text>
          </View>

          <View style={styles.heroStat}>
            <Text style={styles.heroStatValue}>
              {formatMoney(totalEarnings)}
            </Text>
            <Text style={styles.heroStatLabel}>Est. sales</Text>
          </View>
        </View>
      </LinearGradient>
    </View>
  );

  const renderSummaryCards = () => (
    <View style={styles.summaryGrid}>
      <View style={styles.summaryCard}>
        <View style={styles.summaryTopLine}>
          <Ionicons name="pricetag-outline" size={16} color="#111" />
          <Text style={styles.summaryLabel}>Vouchers</Text>
        </View>
        <Text style={styles.summaryValue}>
          {formatNumber(summary.TotalStockAssigned)}
        </Text>
      </View>

      <View style={styles.summaryCard}>
        <View style={styles.summaryTopLine}>
          <Ionicons name="storefront-outline" size={16} color="#111" />
          <Text style={styles.summaryLabel}>Brands</Text>
        </View>
        <Text style={styles.summaryValue}>
          {formatNumber(summary.TotalDeals)}
        </Text>
      </View>

      <View style={styles.summaryCard}>
        <View style={styles.summaryTopLine}>
          <Ionicons name="wallet-outline" size={16} color="#111" />
          <Text style={styles.summaryLabel}>£5 each</Text>
        </View>
        <Text style={styles.summaryValue}>{formatMoney(totalEarnings)}</Text>
      </View>
    </View>
  );

  const renderTabs = () => (
    <View style={styles.tabs}>
      <TouchableOpacity
        style={[styles.tab, activeTab === 'stock' && styles.tabActive]}
        onPress={() => setActiveTab('stock')}
        activeOpacity={0.9}
      >
        <Text style={[styles.tabText, activeTab === 'stock' && styles.tabTextActive]}>
          Live deals
        </Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={[styles.tab, activeTab === 'redemptions' && styles.tabActive]}
        onPress={() => setActiveTab('redemptions')}
        activeOpacity={0.9}
      >
        <Text
          style={[
            styles.tabText,
            activeTab === 'redemptions' && styles.tabTextActive,
          ]}
        >
          Redemptions
        </Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={[styles.tab, activeTab === 'ledger' && styles.tabActive]}
        onPress={() => setActiveTab('ledger')}
        activeOpacity={0.9}
      >
        <Text style={[styles.tabText, activeTab === 'ledger' && styles.tabTextActive]}>
          Ledger
        </Text>
      </TouchableOpacity>
    </View>
  );

  const renderVoucherCard = (voucher, index) => {
    const purchaseURL = getVoucherPurchaseUrl(voucher);
    const redeemed = Number(voucher.Redeemed || 0);
    const remaining = Number(voucher.Remaining || 0);
    const vouchersGiven = Number(voucher.StockAssigned || 0);
    const earnings = redeemed * CREATOR_EARNINGS_PER_REDEMPTION;

    return (
      <View
        key={`${String(voucher.CollabID)}-${String(voucher.BrandName)}-${index}`}
        style={styles.voucherCard}
      >
        <View style={styles.voucherTop}>
          <BrandImage
            image={voucher.BrandProfileImage}
            style={styles.brandImage}
          />

          <View style={styles.brandInfo}>
            <Text style={styles.brandName} numberOfLines={1}>
              {voucher.DisplayBrandName || voucher.BrandName || 'Brand'}
            </Text>

            <Text style={styles.voucherDealText} numberOfLines={2}>
              {formatDealText(voucher)}
            </Text>
          </View>

          <View
            style={[
              styles.statusPill,
              remaining <= 0 && styles.statusPillComplete,
            ]}
          >
            <Text
              style={[
                styles.statusPillText,
                remaining <= 0 && styles.statusPillTextComplete,
              ]}
            >
              {remaining > 0 ? 'Live' : 'Complete'}
            </Text>
          </View>
        </View>

        <View style={styles.voucherMetricRow}>
          <View style={styles.voucherMetric}>
            <Text style={styles.voucherMetricValue}>
              {formatNumber(vouchersGiven)}
            </Text>
            <Text style={styles.voucherMetricLabel}>Given</Text>
          </View>

          <View style={styles.voucherMetric}>
            <Text style={styles.voucherMetricValue}>
              {formatNumber(remaining)}
            </Text>
            <Text style={styles.voucherMetricLabel}>Available</Text>
          </View>

          <View style={styles.voucherMetric}>
            <Text style={styles.voucherMetricValue}>
              {formatNumber(redeemed)}
            </Text>
            <Text style={styles.voucherMetricLabel}>Sold</Text>
          </View>
        </View>

        <View style={styles.saleValueRow}>
          <View>
            <Text style={styles.saleValueLabel}>Estimated sales value</Text>
            <Text style={styles.saleValueText}>
              {formatMoney(earnings)}
            </Text>
          </View>

          <Text style={styles.saleCalculation}>
            {formatNumber(redeemed)} × £5
          </Text>
        </View>

        <TouchableOpacity
          style={styles.purchaseLinkButton}
          activeOpacity={0.88}
          onPress={() => openExternalLink(purchaseURL)}
          disabled={!purchaseURL}
        >
          <View style={styles.purchaseLinkCopy}>
            <Text style={styles.purchaseLinkTitle}>
              Open shareable purchase link
            </Text>
            <Text style={styles.purchaseLinkUrl} numberOfLines={1}>
              {purchaseURL || 'Purchase link unavailable'}
            </Text>
          </View>

          <Ionicons name="arrow-forward-outline" size={18} color="#111" />
        </TouchableOpacity>
      </View>
    );
  };

  const renderEmptyState = (title, copy) => (
    <View style={styles.emptyCard}>
      <View style={styles.emptyIcon}>
        <Ionicons name="pricetag-outline" size={24} color="#111" />
      </View>

      <Text style={styles.emptyTitle}>{title}</Text>
      <Text style={styles.emptyCopy}>{copy}</Text>
    </View>
  );

  const renderStockTab = () => {
    if (liveVouchers.length === 0) {
      return renderEmptyState(
        'No live deals yet',
        'When a brand gives you vouchers, your shareable purchase links will appear here.'
      );
    }

    return (
      <View style={styles.cardList}>
        {liveVouchers.map((voucher, index) => renderVoucherCard(voucher, index))}
      </View>
    );
  };

  const renderRedemptionsTab = () => {
    if (redeemedVouchers.length === 0) {
      return renderEmptyState(
        'No sales yet',
        'When your audience redeems a voucher, your sales activity will show here.'
      );
    }

    return (
      <View style={styles.cardList}>
        {redeemedVouchers.map((voucher, index) =>
          renderVoucherCard(voucher, index)
        )}
      </View>
    );
  };

  const renderLedgerRow = (item, index) => {
    const purchaseURL = getVoucherPurchaseUrl(item);
    const brandName = item.DisplayBrandName || item.BrandName || 'Brand';
    const redeemDate =
      item.RedeemedAt || item.CreatedAt || item.DateCreated || item.Date;

    return (
      <TouchableOpacity
        key={`${String(item.CollabID)}-${index}`}
        style={styles.ledgerRow}
        activeOpacity={0.9}
        onPress={() => openExternalLink(purchaseURL)}
      >
        <BrandImage
          image={item.BrandProfileImage}
          style={styles.ledgerImage}
        />

        <View style={styles.ledgerText}>
          <Text style={styles.ledgerBrand} numberOfLines={1}>
            {brandName}
          </Text>

          <Text style={styles.ledgerMeta} numberOfLines={1}>
            {formatDate(redeemDate)}
            {item.VoucherCode ? ` • ${item.VoucherCode}` : ''}
          </Text>
        </View>

        <View style={styles.ledgerAmount}>
          <Text style={styles.ledgerAmountText}>+£5</Text>
          <Text style={styles.ledgerAmountSub}>sale</Text>
        </View>
      </TouchableOpacity>
    );
  };

  const renderLedgerTab = () => {
    if (ledger.length === 0) {
      return renderEmptyState(
        'Ledger is empty',
        'Each voucher redemption will appear here as an estimated £5 sale.'
      );
    }

    return (
      <View style={styles.ledgerCard}>
        <View style={styles.ledgerHeader}>
          <Text style={styles.ledgerTitle}>Sales ledger</Text>
          <Text style={styles.ledgerSub}>
            Every redemption is currently estimated at £5.
          </Text>
        </View>

        {ledger.map(renderLedgerRow)}
      </View>
    );
  };

  const renderActiveTab = () => {
    if (activeTab === 'stock') return renderStockTab();
    if (activeTab === 'redemptions') return renderRedemptionsTab();

    return renderLedgerTab();
  };

  if (loading) {
    return (
      <View style={styles.loadingPage}>
        <ActivityIndicator size="large" color="#111" />
        <Text style={styles.loadingText}>Loading your vouchers...</Text>
      </View>
    );
  }

  return (
    <View style={styles.outerPage}>
      <View style={styles.yellowStrip} />

      <SafeAreaView style={styles.container}>
        <LinearGradient
          colors={['rgba(253,240,93,0.68)', 'rgba(253,240,93,0)']}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={styles.topGlow}
          pointerEvents="none"
        />

        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
        >
          {renderHeader()}
          {renderPublicPageCard()}

          {errorMessage ? (
            <View style={styles.errorBox}>
              <Text style={styles.errorTitle}>Could not load vouchers</Text>
              <Text style={styles.errorText}>{errorMessage}</Text>
            </View>
          ) : null}

          {renderHero()}
          {renderSummaryCards()}
          {renderTabs()}
          {renderActiveTab()}

          <TouchableOpacity
            style={styles.privacyLink}
            onPress={() =>
              openExternalLink('https://collabbay.com/PrivacyPolicy.html')
            }
          >
            <Text style={styles.privacyText}>
              By using collabbay, you agree to our Privacy Policy.
            </Text>
          </TouchableOpacity>
        </ScrollView>

        <View style={styles.footer}>
          <TouchableOpacity
            style={styles.footerIcon}
            onPress={() => handleFooterPress('Homepage')}
          >
            <Ionicons name="home" size={24} color="black" />
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.footerIcon}
            onPress={() => handleFooterPress('CollabCalendar')}
          >
            <Ionicons name="calendar" size={24} color="black" />
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => handleFooterPress('MyFeed')}
            style={styles.footerMainButton}
          >
            <Ionicons name="film-outline" size={22} color="black" />
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.footerIcon}
            onPress={() => handleFooterPress('UserActivityFeed')}
          >
            <Ionicons name="chatbox-ellipses-outline" size={24} color="black" />
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.footerIcon}
            onPress={() => handleFooterPress('UserRecapPage')}
          >
            <Ionicons name="person" size={24} color="black" />
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </View>
  );
}

const styles = StyleSheet.create({
  outerPage: {
    flex: 1,
    backgroundColor: '#fff',
    alignItems: 'center',
  },
  yellowStrip: {
    height: 12,
    width: '100%',
    maxWidth: 600,
    backgroundColor: '#FDF05D',
  },
  container: {
    flex: 1,
    width: '100%',
    maxWidth: 600,
    backgroundColor: '#fff',
  },
  topGlow: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 205,
    zIndex: 0,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingBottom: 108,
  },
  loadingPage: {
    flex: 1,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 10,
    color: '#555',
    fontSize: 13,
    fontWeight: '700',
  },

  header: {
    width: '100%',
    paddingTop: 20,
    paddingBottom: 14,
    zIndex: 2,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logo: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: '#000',
    marginRight: 10,
  },
  headerText: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 25,
    fontWeight: '900',
    color: '#111',
    letterSpacing: -0.7,
  },
  headerSubtitle: {
    marginTop: 1,
    fontSize: 13,
    color: '#666',
    fontWeight: '700',
  },
  helpButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#f5f5f5',
    alignItems: 'center',
    justifyContent: 'center',
  },

  sharePageCard: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 16,
    marginBottom: 14,
    borderWidth: 1,
    borderColor: '#eeeeee',
    shadowColor: '#000',
    shadowOpacity: 0.045,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 5 },
    elevation: 2,
  },
  sharePageTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 11,
  },
  sharePageBadge: {
    backgroundColor: '#FDF05D',
    borderRadius: 999,
    paddingVertical: 6,
    paddingHorizontal: 10,
  },
  sharePageBadgeText: {
    fontSize: 10,
    fontWeight: '900',
    color: '#111',
    letterSpacing: 0.9,
  },
  sharePageArrow: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#f5f5f5',
    alignItems: 'center',
    justifyContent: 'center',
  },
  sharePageTitle: {
    fontSize: 19,
    color: '#111',
    fontWeight: '900',
    letterSpacing: -0.3,
  },
  sharePageCopy: {
    marginTop: 5,
    color: '#666',
    fontSize: 13,
    fontWeight: '600',
    lineHeight: 19,
  },
  shareUrlRow: {
    marginTop: 13,
    backgroundColor: '#f7f7f7',
    borderRadius: 14,
    paddingVertical: 10,
    paddingHorizontal: 11,
    flexDirection: 'row',
    alignItems: 'center',
  },
  shareUrlText: {
    marginLeft: 7,
    flex: 1,
    fontSize: 11,
    color: '#555',
    fontWeight: '700',
  },

  errorBox: {
    backgroundColor: '#fff4f4',
    borderWidth: 1,
    borderColor: '#ffd0d0',
    borderRadius: 18,
    padding: 14,
    marginBottom: 14,
  },
  errorTitle: {
    color: '#9f1d1d',
    fontWeight: '900',
    fontSize: 14,
  },
  errorText: {
    color: '#9f1d1d',
    fontSize: 12,
    marginTop: 4,
    lineHeight: 17,
  },

  hero: {
    marginBottom: 13,
  },
  heroInner: {
    borderRadius: 27,
    padding: 20,
    overflow: 'hidden',
  },
  heroBadge: {
    color: '#FDF05D',
    fontSize: 10,
    fontWeight: '900',
    letterSpacing: 1.3,
    marginBottom: 9,
  },
  heroTitle: {
    color: '#fff',
    fontSize: 28,
    lineHeight: 32,
    fontWeight: '900',
    letterSpacing: -0.9,
  },
  heroCopy: {
    color: '#d5d5d5',
    marginTop: 9,
    fontSize: 13,
    lineHeight: 19,
    fontWeight: '600',
  },
  heroStatsRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 17,
  },
  heroStat: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.09)',
    borderRadius: 16,
    paddingVertical: 11,
    paddingHorizontal: 10,
  },
  heroStatValue: {
    color: '#fff',
    fontSize: 19,
    fontWeight: '900',
  },
  heroStatLabel: {
    color: '#d8d8d8',
    marginTop: 3,
    fontSize: 10,
    fontWeight: '700',
  },

  summaryGrid: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 14,
  },
  summaryCard: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: 18,
    paddingVertical: 13,
    paddingHorizontal: 11,
    borderWidth: 1,
    borderColor: '#ededed',
  },
  summaryTopLine: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 9,
  },
  summaryLabel: {
    marginLeft: 6,
    fontSize: 10,
    color: '#666',
    fontWeight: '800',
  },
  summaryValue: {
    fontSize: 19,
    fontWeight: '900',
    color: '#111',
  },

  tabs: {
    flexDirection: 'row',
    backgroundColor: '#f3f3f3',
    borderRadius: 999,
    padding: 5,
    marginBottom: 14,
  },
  tab: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 999,
    alignItems: 'center',
  },
  tabActive: {
    backgroundColor: '#111',
  },
  tabText: {
    color: '#555',
    fontSize: 12,
    fontWeight: '900',
  },
  tabTextActive: {
    color: '#fff',
  },

  cardList: {
    gap: 12,
  },
  voucherCard: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 15,
    borderWidth: 1,
    borderColor: '#ededed',
    shadowColor: '#000',
    shadowOpacity: 0.045,
    shadowRadius: 11,
    shadowOffset: { width: 0, height: 5 },
    elevation: 2,
  },
  voucherTop: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  brandImage: {
    width: 56,
    height: 56,
    borderRadius: 17,
    backgroundColor: '#f2f2f2',
    marginRight: 11,
  },
  brandInfo: {
    flex: 1,
  },
  brandName: {
    fontSize: 17,
    fontWeight: '900',
    color: '#111',
  },
  voucherDealText: {
    marginTop: 4,
    fontSize: 12,
    color: '#666',
    fontWeight: '700',
    lineHeight: 17,
  },
  statusPill: {
    backgroundColor: '#FDF05D',
    borderRadius: 999,
    paddingVertical: 6,
    paddingHorizontal: 10,
    marginLeft: 7,
  },
  statusPillComplete: {
    backgroundColor: '#f0f0f0',
  },
  statusPillText: {
    color: '#111',
    fontSize: 10,
    fontWeight: '900',
  },
  statusPillTextComplete: {
    color: '#666',
  },

  voucherMetricRow: {
    flexDirection: 'row',
    gap: 7,
    marginTop: 14,
  },
  voucherMetric: {
    flex: 1,
    backgroundColor: '#f8f8f8',
    borderRadius: 15,
    paddingVertical: 11,
    paddingHorizontal: 10,
  },
  voucherMetricValue: {
    color: '#111',
    fontSize: 17,
    fontWeight: '900',
  },
  voucherMetricLabel: {
    color: '#777',
    fontSize: 10,
    fontWeight: '800',
    marginTop: 3,
  },

  saleValueRow: {
    marginTop: 13,
    borderTopWidth: 1,
    borderTopColor: '#eeeeee',
    paddingTop: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  saleValueLabel: {
    color: '#777',
    fontSize: 10,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  saleValueText: {
    color: '#111',
    marginTop: 4,
    fontSize: 21,
    fontWeight: '900',
  },
  saleCalculation: {
    color: '#666',
    fontSize: 12,
    fontWeight: '800',
    marginBottom: 4,
  },

  purchaseLinkButton: {
    marginTop: 13,
    backgroundColor: '#111',
    borderRadius: 17,
    paddingVertical: 12,
    paddingHorizontal: 13,
    flexDirection: 'row',
    alignItems: 'center',
  },
  purchaseLinkCopy: {
    flex: 1,
    paddingRight: 8,
  },
  purchaseLinkTitle: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '900',
  },
  purchaseLinkUrl: {
    marginTop: 3,
    color: '#bcbcbc',
    fontSize: 10,
    fontWeight: '700',
  },

  emptyCard: {
    backgroundColor: '#fff',
    borderRadius: 23,
    padding: 27,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#ededed',
  },
  emptyIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#FDF05D',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    marginTop: 12,
    fontSize: 17,
    fontWeight: '900',
    color: '#111',
    textAlign: 'center',
  },
  emptyCopy: {
    marginTop: 5,
    fontSize: 13,
    color: '#666',
    lineHeight: 19,
    textAlign: 'center',
    fontWeight: '600',
  },

  ledgerCard: {
    backgroundColor: '#fff',
    borderRadius: 23,
    paddingHorizontal: 14,
    paddingTop: 15,
    paddingBottom: 4,
    borderWidth: 1,
    borderColor: '#ededed',
  },
  ledgerHeader: {
    marginBottom: 9,
  },
  ledgerTitle: {
    fontSize: 18,
    fontWeight: '900',
    color: '#111',
  },
  ledgerSub: {
    marginTop: 4,
    fontSize: 12,
    color: '#666',
    fontWeight: '700',
  },
  ledgerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  ledgerImage: {
    width: 42,
    height: 42,
    borderRadius: 13,
    backgroundColor: '#f2f2f2',
    marginRight: 10,
  },
  ledgerText: {
    flex: 1,
  },
  ledgerBrand: {
    fontSize: 14,
    fontWeight: '900',
    color: '#111',
  },
  ledgerMeta: {
    marginTop: 3,
    fontSize: 12,
    color: '#777',
    fontWeight: '700',
  },
  ledgerAmount: {
    alignItems: 'flex-end',
  },
  ledgerAmountText: {
    color: '#0b7a32',
    fontSize: 16,
    fontWeight: '900',
  },
  ledgerAmountSub: {
    marginTop: 2,
    color: '#777',
    fontSize: 10,
    fontWeight: '800',
  },

  privacyLink: {
    marginTop: 25,
    marginBottom: 8,
  },
  privacyText: {
    fontSize: 11,
    color: 'black',
    textDecorationLine: 'underline',
    textAlign: 'center',
  },

  footer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    position: 'sticky',
    bottom: 0,
    backgroundColor: 'white',
    left: 0,
    right: 0,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  footerIcon: {
    alignItems: 'center',
    justifyContent: 'center',
    width: 42,
    height: 42,
  },
  footerMainButton: {
    backgroundColor: '#FDF05D',
    padding: 10,
    borderRadius: 20,
  },
});